import { useMemo, useState } from 'react'

import type { FilaCartera } from '@/lib/supabase/tipos-bd'
import { pesos } from '@/shared/utilidades/formato'

import estilos from './PaginaCartera.module.css'

/**
 * LA CARTERA QUE QUEDA DE CADA CLIENTE.
 *
 * Johan: «que me separe… la cartera que queda de cada cliente». Una fila
 * por cliente con lo que se le ha facturado (sus FEV), lo que ha pagado, lo
 * que debe y cuántas cuentas tiene abiertas. Se ordena tocando la columna;
 * por omisión, el que más debe arriba. Tocando un cliente se ven sus
 * tarjetas en la vista de cuentas.
 */
type Fila = {
  cliente: string
  facturado: number
  pagado: number
  saldo: number
  abiertas: number
  cuentas: number
  sinFev: number
}

type Columna = 'cliente' | 'facturado' | 'pagado' | 'saldo' | 'abiertas'

export function PorCliente({
  cuentas,
  onCliente,
}: {
  cuentas: FilaCartera[]
  onCliente: (cliente: string) => void
}) {
  const [orden, setOrden] = useState<{ col: Columna; desc: boolean }>({ col: 'saldo', desc: true })

  const filas = useMemo(() => {
    const mapa = new Map<string, Fila>()
    for (const c of cuentas) {
      const k = c.cliente ?? 'Sin cliente'
      const f = mapa.get(k) ?? {
        cliente: k,
        facturado: 0,
        pagado: 0,
        saldo: 0,
        abiertas: 0,
        cuentas: 0,
        sinFev: 0,
      }
      f.facturado += Number(c.valor)
      f.pagado += Number(c.pagado)
      f.saldo += Number(c.saldo)
      f.cuentas += 1
      if (Number(c.saldo) > 0) f.abiertas += 1
      if (c.cot_sin_fev > 0) f.sinFev += 1
      mapa.set(k, f)
    }
    const signo = orden.desc ? -1 : 1
    return [...mapa.values()].sort((a, b) =>
      orden.col === 'cliente'
        ? signo * a.cliente.localeCompare(b.cliente)
        : signo * (a[orden.col] - b[orden.col]) || a.cliente.localeCompare(b.cliente),
    )
  }, [cuentas, orden])

  const total = filas.reduce(
    (t, f) => ({
      facturado: t.facturado + f.facturado,
      pagado: t.pagado + f.pagado,
      saldo: t.saldo + f.saldo,
      abiertas: t.abiertas + f.abiertas,
    }),
    { facturado: 0, pagado: 0, saldo: 0, abiertas: 0 },
  )
  const maximo = Math.max(1, ...filas.map((f) => f.saldo))

  const cabecera = (col: Columna, texto: string, num = true) => (
    <th
      className={num ? estilos.num : undefined}
      aria-sort={orden.col === col ? (orden.desc ? 'descending' : 'ascending') : undefined}
    >
      <button
        type="button"
        className={`${estilos.ordenColumna} ${orden.col === col ? estilos.ordenColumnaViva : ''}`}
        onClick={() =>
          setOrden((o) => ({ col, desc: o.col === col ? !o.desc : col !== 'cliente' }))
        }
      >
        {texto}
        {orden.col === col && <span aria-hidden="true">{orden.desc ? ' ↓' : ' ↑'}</span>}
      </button>
    </th>
  )

  if (filas.length === 0) {
    return <p className={estilos.vacio}>Todavía no hay clientes en cartera.</p>
  }

  return (
    <div className={estilos.porCliente}>
      <div className={estilos.tablaCaja}>
        <table className={estilos.tabla}>
          <thead>
            <tr>
              {cabecera('cliente', 'Cliente', false)}
              {cabecera('facturado', 'Valor (FEV + COT)')}
              {cabecera('pagado', 'Pagado')}
              {cabecera('saldo', 'Debe')}
              {cabecera('abiertas', 'Abiertas')}
            </tr>
          </thead>
          <tbody>
            {filas.map((f) => (
              <tr key={f.cliente}>
                <td>
                  <button
                    type="button"
                    className={estilos.clienteBoton}
                    onClick={() => onCliente(f.cliente)}
                    title="Ver sus cuentas"
                  >
                    {f.cliente}
                  </button>
                  <span className={estilos.barraCliente} aria-hidden="true">
                    <span style={{ width: `${(f.saldo / maximo) * 100}%` }} />
                  </span>
                  {f.sinFev > 0 && (
                    <span className={estilos.clienteNota}>
                      {f.sinFev}{' '}
                      {f.sinFev === 1
                        ? 'servicio con COT por pasar a FEV'
                        : 'servicios con COT por pasar a FEV'}
                    </span>
                  )}
                </td>
                <td className={estilos.num}>{pesos(f.facturado)}</td>
                <td className={estilos.num}>{pesos(f.pagado)}</td>
                <td className={`${estilos.num} ${estilos.saldoCelda}`}>{pesos(f.saldo)}</td>
                <td className={estilos.num}>
                  {f.abiertas} <span className={estilos.deCuantas}>de {f.cuentas}</span>
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td>
                Total · {filas.length} {filas.length === 1 ? 'cliente' : 'clientes'}
              </td>
              <td className={estilos.num}>{pesos(total.facturado)}</td>
              <td className={estilos.num}>{pesos(total.pagado)}</td>
              <td className={`${estilos.num} ${estilos.saldoCelda}`}>{pesos(total.saldo)}</td>
              <td className={estilos.num}>{total.abiertas}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  )
}
